// @ts-nocheck
import React, { useState, useEffect } from 'react';
import { useRecordContext, useNotify } from 'react-admin';
import {
	Card,
	CardContent,
	Typography,
	Button,
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	TextField as MuiTextField,
	Box,
} from '@mui/material';

interface TokenConfigCardProps {
	configType: string;
	title: string;
	tokenLabel: string;
	tokenPlaceholder?: string;
	helperText?: React.ReactNode;
}

/**
 * Carte de configuration pour les intégrations à un seul token
 * (PAT GitHub, etc.). Stocke `config_data = { access_token }`
 * dans `mcp_member_configs` via l'API mcp-internal.
 */
export const TokenConfigCard = ({
	configType,
	title,
	tokenLabel,
	tokenPlaceholder,
	helperText,
}: TokenConfigCardProps) => {
	const record = useRecordContext();
	const notify = useNotify();
	const [config, setConfig] = useState<any>(null);
	const [loading, setLoading] = useState(true);
	const [editing, setEditing] = useState(false);
	const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
	const [accessToken, setAccessToken] = useState('');

	useEffect(() => {
		if (record?.id) {
			loadConfig();
		}
	}, [record?.id, configType]);

	const loadConfig = async () => {
		if (!record?.id) return;

		setLoading(true);
		try {
			const token = localStorage.getItem('token');
			const w: any = window;
			const response = await fetch(
				`${w.API_MCP_INTERNE_URL}/api/configs?member_id=${record.id}&config_type=${configType}`,
				{
					headers: {
						Authorization: `Bearer ${token}`,
					},
				}
			);

			if (response.ok) {
				const data = await response.json();
				if (data.length > 0) {
					const cfg = data[0];
					setConfig(cfg);
					setAccessToken(cfg.config_data.access_token || '');
				}
			}
		} catch (error) {
			console.error(`Erreur lors du chargement de la config ${title}:`, error);
		} finally {
			setLoading(false);
		}
	};

	const handleSave = async () => {
		if (!record?.id) return;

		try {
			const token = localStorage.getItem('token');
			const w: any = window;

			const payload = {
				member_id: record.id,
				config_type: configType,
				config_name: `${title} Configuration`,
				config_data: {
					access_token: accessToken,
				},
				is_active: true,
				is_default: true,
			};

			const url = config
				? `${w.API_MCP_INTERNE_URL}/api/configs/${config.id}`
				: `${w.API_MCP_INTERNE_URL}/api/configs`;
			const method = config ? 'PUT' : 'POST';

			const response = await fetch(url, {
				method,
				headers: {
					Authorization: `Bearer ${token}`,
					'Content-Type': 'application/json',
				},
				body: JSON.stringify(payload),
			});

			if (!response.ok) {
				throw new Error('Erreur lors de la sauvegarde');
			}

			notify(`Configuration ${title} sauvegardée avec succès`, { type: 'success' });
			setEditing(false);
			loadConfig();
		} catch (error) {
			notify(`Erreur lors de la sauvegarde de la configuration ${title}`, {
				type: 'error',
			});
			console.error(error);
		}
	};

	const handleDeleteConfirm = async () => {
		if (!config?.id || !record?.id) return;

		setDeleteDialogOpen(false);

		try {
			const token = localStorage.getItem('token');
			const w: any = window;

			const response = await fetch(
				`${w.API_MCP_INTERNE_URL}/api/configs/${config.id}`,
				{
					method: 'DELETE',
					headers: {
						Authorization: `Bearer ${token}`,
					},
				}
			);

			if (!response.ok) {
				throw new Error('Erreur lors de la suppression');
			}

			notify(`Configuration ${title} supprimée avec succès`, { type: 'success' });
			setConfig(null);
			setAccessToken('');
			setEditing(false);
		} catch (error) {
			notify(`Erreur lors de la suppression de la configuration ${title}`, {
				type: 'error',
			});
			console.error(error);
		}
	};

	if (loading) {
		return (
			<Card>
				<CardContent>
					<Typography variant="h6" gutterBottom>
						Configuration {title}
					</Typography>
					<Typography color="text.secondary">Chargement...</Typography>
				</CardContent>
			</Card>
		);
	}

	return (
		<Card>
			<CardContent>
				<div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
					<Typography variant="h6">Configuration {title}</Typography>
					{!editing && (
						<Button
							variant="contained"
							color="primary"
							size="small"
							onClick={() => setEditing(true)}
						>
							{config ? 'Modifier' : 'Créer'}
						</Button>
					)}
				</div>

				{!editing && config ? (
					<Box>
						<Typography variant="body2" color="text.secondary" gutterBottom>
							<strong>{tokenLabel}:</strong> ••••••••
						</Typography>
					</Box>
				) : !editing ? (
					<Typography color="text.secondary">
						Aucune configuration {title} pour ce membre.
					</Typography>
				) : (
					<Box>
						<MuiTextField
							fullWidth
							margin="normal"
							label={tokenLabel}
							type="password"
							placeholder={tokenPlaceholder}
							value={accessToken}
							onChange={(e) => setAccessToken(e.target.value)}
							helperText={helperText}
							required
						/>
						<Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
							<Button
								variant="contained"
								color="primary"
								onClick={handleSave}
								fullWidth
							>
								Sauvegarder
							</Button>
							<Button
								variant="outlined"
								onClick={() => {
									setEditing(false);
									setAccessToken(config?.config_data.access_token || '');
								}}
								fullWidth
							>
								Annuler
							</Button>
						</Box>
						{config && (
							<Button
								variant="text"
								color="error"
								onClick={() => setDeleteDialogOpen(true)}
								fullWidth
								sx={{ mt: 1 }}
							>
								Supprimer la configuration
							</Button>
						)}
					</Box>
				)}

				<Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
					<DialogTitle>Supprimer la configuration {title}</DialogTitle>
					<DialogContent>
						<Typography>
							Êtes-vous sûr de vouloir supprimer cette configuration {title} ? Cette action est
							irréversible.
						</Typography>
					</DialogContent>
					<DialogActions>
						<Button onClick={() => setDeleteDialogOpen(false)}>Annuler</Button>
						<Button onClick={handleDeleteConfirm} color="error" variant="contained">
							Supprimer
						</Button>
					</DialogActions>
				</Dialog>
			</CardContent>
		</Card>
	);
};
